/**
 * Convert API field mapping detail response to UI format
 * This combines data from API with target fields metadata
 */

import mockTargetFields from "../../../../mocks/targetFields.json";
import mockFieldMappingsTemplate from "../../../../mocks/fieldMappings.json";

/**
 * Create default field mappings from target fields metadata
 * Used when API returns no data or for new profiles
 * @param {Array} targetFieldsMetadata - Target fields metadata
 * @returns {Array} - Array of field mappings with empty values
 */
export const createDefaultFieldMappings = (targetFieldsMetadata) => {
  if (!targetFieldsMetadata || !Array.isArray(targetFieldsMetadata)) {
    return mockFieldMappingsTemplate || [];
  }

  return targetFieldsMetadata.map((field) => ({
    targetField: {
      apiName: field.apiName,
      label: field.label,
      type: field.type,
      isCustom: field.isCustom || false,
      required: field.required || false,
      picklistValues: field.picklistValues || [],
    },
    sourceFieldApiName: "",
    defaultValue: "",
    isGlobal: false,
    isSaved: false,
  }));
};

/**
 * Convert API mapping_json and picklist_mapping_json to UI fieldMappings format
 * @param {Object} apiData - API response data with mapping_json and picklist_mapping_json
 * @param {Array} targetFields - Array of target field metadata from mocks/targetFields.json
 * @returns {Array} - Array of field mappings in UI format
 */
export const convertApiDetailToFieldMappings = (apiData, targetFieldsMetadata) => {
  // Check if mapping_json exists and is an array
  if (!apiData || !apiData.mapping_json || !Array.isArray(apiData.mapping_json)) {
    console.log("No valid mapping_json found, using default field mappings");
    return createDefaultFieldMappings(targetFieldsMetadata);
  }

  const { mapping_json } = apiData;

  // If mapping_json is empty, return default mappings
  if (mapping_json.length === 0) {
    console.log("Empty mapping_json, using default field mappings");
    return createDefaultFieldMappings(targetFieldsMetadata);
  }

  // Create a map of target fields by apiName for quick lookup
  const targetFieldsMap = {};
  if (targetFieldsMetadata && Array.isArray(targetFieldsMetadata)) {
    targetFieldsMetadata.forEach((field) => {
      if (field.apiName) {
        targetFieldsMap[field.apiName] = field;
      }
    });
  }

  // Convert each mapping_json item to UI format
  const fieldMappings = mapping_json.map((apiMapping) => {
    const targetFieldApiName = apiMapping.targetField;
    const targetFieldMetadata = targetFieldsMap[targetFieldApiName];

    if (!targetFieldMetadata) {
      console.warn(`Target field metadata not found for: ${targetFieldApiName}`);
      return null;
    }

    // Build the UI mapping object
    const uiMapping = {
      targetField: {
        apiName: targetFieldMetadata.apiName,
        label: targetFieldMetadata.label,
        type: targetFieldMetadata.type,
        isCustom: targetFieldMetadata.isCustom || false,
        required: targetFieldMetadata.required || false,
        picklistValues: targetFieldMetadata.picklistValues || [],
      },
      sourceFieldApiName: apiMapping.sourceField || "",
      defaultValue: apiMapping.defaultValue || "",
      isGlobal: apiMapping.isGlobal || false,
      isSaved: true, // Data from API is already saved
    };

    return uiMapping;
  }).filter(Boolean); // Remove null entries

  return fieldMappings;
};

/**
 * Convert picklist_mapping_json from API to UI format
 * @param {Object} picklistMappingJson - Object with field names as keys
 * @returns {Object} - Object with field names as keys and arrays of picklist mappings
 */
export const convertApiPicklistMappingsToUi = (picklistMappingJson) => {
  if (!picklistMappingJson || typeof picklistMappingJson !== 'object') {
    return {};
  }

  const result = {};

  Object.keys(picklistMappingJson).forEach((fieldApiName) => {
    const mappings = picklistMappingJson[fieldApiName];

    if (Array.isArray(mappings)) {
      result[fieldApiName] = mappings.map((mapping) => ({
        targetField: mapping.targetField,
        targetValue: mapping.targetValue,
        sourceValue: mapping.sourceValue,
      }));
    }
  });

  return result;
};

/**
 * Convert pipeline_picklist_mappings from API to UI format
 * @param {Array} pipelinePicklistMappings - Array of pipeline picklist mapping objects from API
 * @returns {Object} - Object with pipelineId as keys and picklist mappings as values
 */
export const convertApiPipelinePicklistMappingsToUi = (pipelinePicklistMappings) => {
  if (!pipelinePicklistMappings || !Array.isArray(pipelinePicklistMappings)) {
    return {};
  }

  const result = {};

  pipelinePicklistMappings.forEach((pipelineMapping) => {
    const pipelineId = pipelineMapping.pipeline_id;
    const picklistMappingJson = pipelineMapping.picklist_mapping_json;

    if (pipelineId && picklistMappingJson) {
      result[pipelineId] = picklistMappingJson;
    }
  });

  return result;
};

/**
 * Main converter function to convert complete API response to UI format
 * @param {Object} apiResponse - Complete API response from getFieldMappingDetail
 * @param {Array} targetFieldsMetadata - Target fields metadata
 * @returns {Object} - Object with fieldMappings, picklistMappings, and pipelinePicklistMappings
 */
export const convertApiResponseToUiData = (apiResponse, targetFieldsMetadata) => {
  if (!apiResponse || !apiResponse.data) {
    return {
      fieldMappings: [],
      picklistMappings: {},
      pipelinePicklistMappings: {},
    };
  }

  const { data } = apiResponse;

  return {
    fieldMappings: convertApiDetailToFieldMappings(data, targetFieldsMetadata),
    picklistMappings: convertApiPicklistMappingsToUi(data.picklist_mapping_json),
    pipelinePicklistMappings: convertApiPipelinePicklistMappingsToUi(data.pipeline_picklist_mappings),
  };
};
